import { Plus, Sparkles } from 'lucide-react'

import { Tooltip } from '@/components/ui'
import { cn } from '@/utils'
import { useChatSidebarSession } from '../../hooks'
import { SIDEBAR_COLLAPSED_WIDTH } from '../../utils/chatPrefs'
import ChatSessionsPopover from '../ChatSessionsPopover'
import ChatIntroCallout from './ChatIntroCallout'

interface CollapsedRailProps {
  slug: string
  onExpand: () => void
  showIntro: boolean
  onDismissIntro: () => void
}

// Daystrom-branded vertical rail shown when the chat panel is
// collapsed. The gradient sparkle mark mirrors the expanded header's
// DaystromWordmark so the rail reads as "the chat panel, just closed."
//
// Quick actions sit below the brand:
// - "New chat" expands the panel and starts a fresh session.
// - "Conversation history" opens its popover in place — the chat
//   panel stays collapsed. Selecting a session from inside the
//   popover does expand the panel (otherwise nothing visible changes).
export default function CollapsedRail({
  slug,
  onExpand,
  showIntro,
  onDismissIntro,
}: CollapsedRailProps) {
  const { selectSession, clearSession, createAndOpenSession } =
    useChatSidebarSession(slug)

  async function handleNewChat() {
    onExpand()
    await createAndOpenSession()
  }

  function handleSelectSession(sessionId: string) {
    onExpand()
    selectSession(sessionId)
  }

  return (
    <aside
      className="relative hidden h-full shrink-0 flex-col items-center gap-2 border-l bg-background py-4 lg:flex"
      style={{ width: SIDEBAR_COLLAPSED_WIDTH }}
    >
      {showIntro && (
        <ChatIntroCallout onTry={onExpand} onDismiss={onDismissIntro} />
      )}

      <Tooltip content="Open chat" side="left">
        <button
          type="button"
          onClick={onExpand}
          aria-label="Open chat"
          className="group flex cursor-pointer flex-col items-center gap-1 rounded-lg p-0.5 transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <DaystromMark />
          <span className="text-[10px] font-medium tracking-wide text-muted-foreground transition-colors group-hover:text-foreground">
            Chat
          </span>
        </button>
      </Tooltip>

      <div className="my-1 h-px w-6 bg-border" aria-hidden="true" />

      <RailIconButton
        label="New chat"
        onClick={handleNewChat}
        icon={<Plus className="h-4 w-4" />}
      />
      <ChatSessionsPopover
        slug={slug}
        onSelect={handleSelectSession}
        onCreateNew={handleNewChat}
        onArchiveCurrent={clearSession}
        triggerClassName={RAIL_ICON_BUTTON_CLASSES}
      />
    </aside>
  )
}

function DaystromMark() {
  return (
    <span
      className={cn(
        'relative flex h-9 w-9 items-center justify-center rounded-lg',
        'bg-gradient-to-br from-cyan-300 via-pink-300 to-violet-500 bg-[length:220%_220%]',
        'text-white shadow-[0_0_14px_rgba(56,189,248,0.55)] ring-1 ring-white/40',
        'animate-daystrom-shine motion-reduce:animate-none',
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-1 top-0.5 h-1/2 rounded-md bg-white/30 blur-[1px]"
      />
      <Sparkles
        className="relative h-5 w-5 drop-shadow-[0_1px_3px_rgba(0,0,0,0.85)]"
        strokeWidth={2.5}
      />
    </span>
  )
}

const RAIL_ICON_BUTTON_CLASSES =
  'flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'

interface RailIconButtonProps {
  label: string
  onClick: () => void | Promise<void>
  icon: React.ReactNode
}

function RailIconButton({ label, onClick, icon }: RailIconButtonProps) {
  return (
    <Tooltip content={label} side="left">
      <button
        type="button"
        onClick={() => {
          void onClick()
        }}
        aria-label={label}
        className={RAIL_ICON_BUTTON_CLASSES}
      >
        {icon}
      </button>
    </Tooltip>
  )
}
